import axiosInstance, { logError } from 'src/utils/ApiHelper';
import { GLOBAL } from '../constants/Global';
import { GLOBAL_STATUS } from '../constants/Global';
import { SESSION_STORAGE_KEYS } from 'src/constants/StorageKeys';
import { APP_NAME } from 'src/components/constant';
import { janusServiceUrl } from './clickStream.constant';
import { getDeviceId } from 'src/utils/FingerPrintJS';
import { IClickstreamEvent } from './clickStream.type';
import { getServerTime } from 'src/utils/DateHelper';
import {
  addEventToDB,
  getEventsCountFromDB,
  getAllEventsFromDB,
  clearEventsFromDB,
  clearSpecificEventsFromDB
} from 'src/utils/ClickstreamDB';

const MAX_BUFFER_SIZE_FOR_API = 10;
const MAX_TIME_BETWEEN_FLUSHES_MS = 3 * 60 * 1000; // 3 minutes in milliseconds
const MAX_RETRY_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY = 5000; // 5 seconds
const MAX_RETRY_DELAY = 60000; // 1 minute

let lastFlushTime = Date.now();
let isFlushInProgress = false;
let flushPromise: Promise<void> | null = null;
let retryAttempts = 0;
let retryTimeout: ReturnType<typeof setTimeout> | null = null;
let isOnline = navigator.onLine;

window.addEventListener(GLOBAL_STATUS.ONLINE, () => {
  isOnline = true;
  // When we come back online, try to flush events
  if (retryTimeout) {
    clearTimeout(retryTimeout);
    retryTimeout = null;
  }
  retryAttempts = 0;
  fireClickstreamEvents();
});

window.addEventListener(GLOBAL_STATUS.OFFLINE, () => {
  isOnline = false;
});

type ClickstreamParams = {
  loanID?: string;
  customerId?: string;
};

export const GLOBAL_CLICKSTREAM_PARAMS: ClickstreamParams = {};

export const registerGlobalClickstreamParams = (params: Record<string, string>) => {
  GLOBAL_CLICKSTREAM_PARAMS['customerId'] = params?.customerId;
  GLOBAL_CLICKSTREAM_PARAMS['loanID'] = params?.loanId;
};

const shouldFlushEvents = async (isPriority = false): Promise<boolean> => {
  if (isPriority) return true;

  const count = await getEventsCountFromDB();
  const timeSinceLastFlush = Date.now() - lastFlushTime;

  const shouldFlushBySize = count >= MAX_BUFFER_SIZE_FOR_API;
  const shouldFlushByTime = count > 0 && timeSinceLastFlush >= MAX_TIME_BETWEEN_FLUSHES_MS;
  const shouldFlush = shouldFlushBySize || shouldFlushByTime;

  return shouldFlush;
};

const sanitizeAttributes = (attrs: Record<string, unknown>): Record<string, unknown> => {
  const sanitized: Record<string, unknown> = {};
  Object.entries(attrs).forEach(([key, value]) => {
    if (value instanceof Response) {
      sanitized[key] = '[Response]';
      return;
    }
    try {
      structuredClone(value);
      sanitized[key] = value;
    } catch {
      try {
        sanitized[key] = JSON.stringify(value);
      } catch {
        sanitized[key] =
          value && (value as any).constructor
            ? `[${(value as any).constructor.name}]`
            : '[Unserializable]';
      }
      logError(
        `[ClickstreamService] Non-serializable attribute "${key}" was converted for storage.`
      );
    }
  });
  return sanitized;
};

export const addClickstreamEvent = async (
  eventDesc: { name: string; description: string },
  attributes: Record<string, unknown> = {},
  isPriority = false
) => {
  const { DEVICE_ID: deviceId = getDeviceId(), AGENT_ID: agentId } = GLOBAL;
  const name = eventDesc?.name;
  const description = eventDesc?.description;
  const currentTabId = sessionStorage.getItem(SESSION_STORAGE_KEYS.TAB_ID);
  const eventAttributes: any = { ...attributes, currentTabId };
  const { loanID, customerId } = GLOBAL_CLICKSTREAM_PARAMS;
  const { LONGHORN_SESSION_IDENTIFIER } = GLOBAL;
  if (loanID) eventAttributes['loanId'] = loanID;
  if (customerId) eventAttributes['customerId'] = customerId;
  if (LONGHORN_SESSION_IDENTIFIER)
    eventAttributes['longhornSessionVerifier'] = LONGHORN_SESSION_IDENTIFIER;

  if (window?.clientIp) {
    eventAttributes['clientIp'] = window?.clientIp || '';
  }

  const sanitizedEventAttributes = sanitizeAttributes(eventAttributes);

  const event = {
    event_name: name,
    description,
    deviceId,
    agentId,
    customerId,
    attributes: sanitizedEventAttributes,
    timestamp: getServerTime()
  };

  // Wait for any ongoing flush to complete before adding new event
  if (flushPromise) {
    await flushPromise;
  }

  const currentCount = await getEventsCountFromDB();
  if (currentCount >= MAX_BUFFER_SIZE_FOR_API && !isPriority) {
    await fireClickstreamEvents();
  }

  await addEventToDB(event);

  if (await shouldFlushEvents(isPriority)) {
    fireClickstreamEvents();
  }
};

const getPayload = (events: IClickstreamEvent[]) => {
  const { agentId, deviceId = getDeviceId(), customerId } = events[events.length - 1];
  return {
    app: { name: APP_NAME },
    events,
    metadata: {
      agentId,
      deviceId
    },
    client_ts: getServerTime(),
    source: APP_NAME,
    user: {
      customer_id: customerId
    }
  };
};

const fireClickstreamEvents = async () => {
  // Prevent multiple simultaneous flushes
  if (isFlushInProgress) {
    return flushPromise;
  }

  // Don't attempt to send events if we know we're offline
  if (!isOnline) {
    return Promise.resolve();
  }

  isFlushInProgress = true;

  flushPromise = (async () => {
    try {
      const dbEvents = await getAllEventsFromDB();

      if (!dbEvents.length) {
        retryAttempts = 0; // Reset retry attempts when no events to process
        return;
      }

      const batchSize = MAX_BUFFER_SIZE_FOR_API;
      const eventsToProcess = dbEvents.slice(0, batchSize);

      const eventIdsToDelete = eventsToProcess
        .map(event => (event as any).id)
        .filter(id => id !== undefined);

      const events = eventsToProcess.map((item: IClickstreamEvent) => {
        const {
          deviceId = getDeviceId(),
          agentId,
          event_name,
          attributes,
          timestamp,
          customerId
        } = item;
        return {
          deviceId,
          agentId,
          event_name,
          attributes,
          timestamp,
          customerId
        };
      });

      const payload = getPayload(events as IClickstreamEvent[]);
      await axiosInstance.post(janusServiceUrl, payload, {
        headers: { donotHandleError: true }
      });

      if (eventIdsToDelete.length > 0) {
        await clearSpecificEventsFromDB(eventIdsToDelete);
      }
      lastFlushTime = Date.now();
      retryAttempts = 0; // Reset retry attempts on success

      // If there are more events remaining, schedule another flush
      const remainingCount = await getEventsCountFromDB();
      if (remainingCount > 0) {
        setTimeout(() => fireClickstreamEvents(), 100);
      }
    } catch (error) {
      logError(error);

      if (retryAttempts < MAX_RETRY_ATTEMPTS) {
        retryAttempts++;
        const delay = Math.min(
          INITIAL_RETRY_DELAY * Math.pow(2, retryAttempts - 1),
          MAX_RETRY_DELAY
        );

        if (retryTimeout) {
          clearTimeout(retryTimeout);
        }

        retryTimeout = setTimeout(() => {
          isFlushInProgress = false;
          flushPromise = null;
          fireClickstreamEvents();
        }, delay);
      }
    } finally {
      if (!retryTimeout) {
        isFlushInProgress = false;
        flushPromise = null;
      }
    }
  })();

  return flushPromise;
};
